import axios, { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { sortBy } from 'lodash'

import { BoundingBox, Citation } from '../landing/dataStructures'

const host = process.env.REACT_APP_API_HOST || 'https://wwwdev.bodc.ac.uk/data/published_data_library/api'

export async function getCitation (uuid: string) {
  try {
    const decodedUuid = decodeURIComponent(uuid)
    const response = await axios.get(`${host}/doi/${decodedUuid}`)
    return parseData(response.data)
  } catch (ex) {
    if (ex instanceof AxiosError) {
      throw Error('Unable to retrieve data from server.')
    } else if (ex instanceof ParseError) {
      throw Error('Error parsing data from server.')
    } else {
      throw Error('An unexpected error has occurred.')
    }
  }
}

export async function getSchema (uuid: string) {
  try {
    const decodedUuid = decodeURIComponent(uuid)
    const response = await axios.get(`${host}/schema/${decodedUuid}`)
    return response.data
  } catch (ex) {
    if (ex instanceof AxiosError) {
      throw Error('Unable to retrieve schema from server.')
    } else {
      throw Error('An unexpected error has occurred.')
    }
  }
}

class ParseError extends Error {
  constructor (message: string) {
    super(message)
    this.name = 'ParseError'
  }
}

const parseData = (rawData: any) => {
  try {
    const parsedData: Citation = {
      shortTitle: rawData.shortTitle,
      title: rawData.title,
      authors: sortBy(rawData.authors.names || [], 'rank').map(x => ({ name: x.name, rank: x.rank, organisations: [x.organisation] })),
      authorOrganisations: sortBy(rawData.authors.organisations, 'displayIndex') || [],
      contributors: rawData.contributors ? sortBy(rawData.contributors.name || [], 'rank').map(x => ({ name: x.name, rank: x.rank, organisations: [x.organisation] })) : [],
      contributorOrganisations: rawData.contributors ? sortBy(rawData.contributors.organisations, 'displayIndex') || [] : [],
      abstract: rawData.abstract,
      subjects: rawData.subject,
      temporalCoverage: {
        start: dayjs(rawData.temporalCoverage.start, 'YYYY-MM-DD'),
        end: dayjs(rawData.temporalCoverage.end, 'YYYY-MM-DD')
      },
      licence: rawData.licence ? { name: rawData.licence.name, url: rawData.licence.url } : null,
      publicationDate: dayjs(rawData.publicationDate, 'YYYY-MM-DD'),
      doi: rawData.doi,
      publisher: rawData.publisher,
      isMostRecent: rawData.replacedBy.length === 0,
      replaces: rawData.replaces.reverse(),
      replacedBy: rawData.replacedBy.reverse(),
      citation: rawData.citation,
      edmed: rawData.edmed,
      fileFormats: rawData.fileFormats.map((x: {name: string}) => x.name),
      spatialCoverage: rawData.spatialCoverage.map((x: {name: string}) => x.name),
      geolocations: rawData.geolocations.map(
        (x: BoundingBox) => new BoundingBox(x.westCorner, x.eastCorner, x.northCorner, x.southCorner)
      ),
      languages: rawData.languages,
      downloadLinks: rawData.downloadLinks,
      endEmbargo: rawData.endEmbargo
    }
    return parsedData
  } catch (ex) {
    if (ex instanceof Error) {
      throw new ParseError(ex.message)
    } else {
      throw new ParseError('')
    }
  }
}
